import React, { useEffect, useMemo, useState } from "react";
import { Container, Button, Badge, Form } from "react-bootstrap";
import { useNavigate, useRevalidator } from "react-router-dom";
import { PageBar } from "../../Controls/PageBar";
import { createApiService } from "../../shared/apiConfig";
import { useParams } from "react-router-dom";
import AdminOperatorIntegrationEnrollModal from "./AdminOperatoroIntegrationEnrollModal";
import { IntegrationPartyApi, ServerTransferEnrolledIntegrationParty, ServerTransferIntegrationParty } from "../../shared/api/axios-client";

const integrationPartyApi = createApiService(IntegrationPartyApi);

type ViewMode = 'byFeature' | 'byFacility';

export function AdminOperatorIntegrationListPage() {
    const nav = useNavigate();
    const { revalidate } = useRevalidator();
    const { operatorId } = useParams<{ operatorId?: string }>();

    const [viewMode, setViewMode] = useState<ViewMode>('byFeature');
    const [integrations, setIntegrations] = useState<ServerTransferIntegrationParty[]>([]);

    // By-feature state
    const [facilities, setFacilities] = useState<ServerTransferEnrolledIntegrationParty[]>([]);
    const [selectedIntegration, setSelectedIntegration] = useState<ServerTransferIntegrationParty | undefined>(undefined);
    const [facilitySearch, setFacilitySearch] = useState('');
    const [facilityFilter, setFacilityFilter] = useState<'all' | 'active' | 'inactive'>('all');
    const [featureSearch, setFeatureSearch] = useState('');
    const [mobileShowFacilities, setMobileShowFacilities] = useState(false);

    // By-facility state
    const [enrollmentCache, setEnrollmentCache] = useState<Map<number, ServerTransferEnrolledIntegrationParty[]>>(new Map());
    const [selectedFacilityId, setSelectedFacilityId] = useState<number | undefined>(undefined);
    const [facilityListSearch, setFacilityListSearch] = useState('');
    const [featurePanelSearch, setFeaturePanelSearch] = useState('');
    const [featurePanelFilter, setFeaturePanelFilter] = useState<'all' | 'active' | 'inactive'>('all');
    const [mobileShowFeatures, setMobileShowFeatures] = useState(false);

    // Shared modal state
    const [showEditModal, setShowEditModal] = useState(false);
    const [editInitialData, setEditInitialData] = useState<ServerTransferEnrolledIntegrationParty | undefined>(undefined);
    const [editIntegrationParty, setEditIntegrationParty] = useState<ServerTransferIntegrationParty | undefined>(undefined);
    // When editing from facility view, we need the integration id
    const [editIntegrationId, setEditIntegrationId] = useState<number | undefined>(undefined);

    const loadIntegrations = async () => {
        const response = await integrationPartyApi.listIntegrationParty({});
        if (response.data) {
            setIntegrations(response.data.items!);
        }
    };

    const loadFacilitiesForIntegration = async (integrationId: number) => {
        const response = await integrationPartyApi.listEnrolledIntegrationParty({
            integrationPartyId: integrationId,
            operatorId: parseInt(operatorId!)
        });
        setFacilities(response.data?.items ?? []);
    };

    const loadAllEnrollments = async () => {
        const response = await integrationPartyApi.listEnrolledIntegrationParty({
            operatorId: parseInt(operatorId!)
        });
        const items = response.data?.items ?? [];
        const newCache = new Map<number, ServerTransferEnrolledIntegrationParty[]>();
        for (const item of items) {
            if (item.facilityId != null) {
                if (!newCache.has(item.facilityId)) newCache.set(item.facilityId, []);
                newCache.get(item.facilityId)!.push(item);
            }
        }
        setEnrollmentCache(newCache);
    };

    const refreshSingleEnrollment = async (facilityId: number, integrationPartyId: number) => {
        const response = await integrationPartyApi.listEnrolledIntegrationParty({
            operatorId: parseInt(operatorId!),
            facilityId,
            integrationPartyId,
        });
        const fresh = response.data?.items?.[0];
        setEnrollmentCache(prev => {
            const existing = prev.get(facilityId) ?? [];
            let updated: ServerTransferEnrolledIntegrationParty[];
            if (fresh) {
                const idx = existing.findIndex(e => e.integrationPartyId === integrationPartyId);
                updated = idx >= 0
                    ? existing.map((e, i) => i === idx ? fresh : e)
                    : [...existing, fresh];
            } else {
                updated = existing.filter(e => e.integrationPartyId !== integrationPartyId);
            }
            return new Map(prev).set(facilityId, updated);
        });
    };

    useEffect(() => {
        loadIntegrations();
    }, []);

    const handleViewModeChange = (mode: ViewMode) => {
        setViewMode(mode);
        if (mode === 'byFacility') {
            setSelectedFacilityId(undefined);
            setFacilityListSearch('');
            setFeaturePanelSearch('');
            setFeaturePanelFilter('all');
            setMobileShowFeatures(false);
            loadAllEnrollments();
        }
    };

    // ── By-feature handlers ───────────────────────────────────────────────────

    const onSelectIntegration = async (integration: ServerTransferIntegrationParty) => {
        setSelectedIntegration(integration);
        setFacilitySearch('');
        setFacilityFilter('all');
        setMobileShowFacilities(true);
        await loadFacilitiesForIntegration(integration.id!);
    };

    const enrollIntegration = (facility: ServerTransferEnrolledIntegrationParty) => {
        if (!selectedIntegration?.id) return;
        integrationPartyApi.enrollIntegrationParty({
            id: selectedIntegration.id,
            serverRequestsEnrollIntegrationPartyRequest: {
                facilityId: facility.facilityId!,
                externalId: facility.externalId ?? '',
                token: facility.token ?? '',
                apiSecret: facility.apiSecret ?? '',
                isActive: !facility.isActive
            }
        }).then(async () => {
            revalidate();
            await loadFacilitiesForIntegration(selectedIntegration.id!);
        });
    };

    const editIntegration = (data: ServerTransferEnrolledIntegrationParty) => {
        setEditInitialData(data);
        setEditIntegrationParty(selectedIntegration);
        setEditIntegrationId(selectedIntegration?.id);
        setShowEditModal(true);
    };

    const onModalSave = async (integration: ServerTransferEnrolledIntegrationParty) => {
        const integId = editIntegrationId ?? selectedIntegration?.id;
        if (!integId) return;
        integrationPartyApi.enrollIntegrationParty({
            id: integId,
            serverRequestsEnrollIntegrationPartyRequest: {
                facilityId: integration.facilityId!,
                externalId: integration.externalId,
                token: integration.token,
                apiSecret: integration.apiSecret,
                isActive: integration.isActive === undefined
                    ? false
                    : typeof integration.isActive === "boolean"
                        ? integration.isActive
                        : integration.isActive === "true"
            }
        }).then(async () => {
            revalidate();
            setShowEditModal(false);
            if (viewMode === 'byFeature' && selectedIntegration?.id) {
                await loadFacilitiesForIntegration(selectedIntegration.id);
            } else if (viewMode === 'byFacility') {
                const facilityId = integration.facilityId ?? selectedFacilityId;
                if (facilityId != null) await refreshSingleEnrollment(facilityId, integId);
            }
        });
    };

    // ── By-facility handlers ──────────────────────────────────────────────────

    const onSelectFacility = (facilityId: number) => {
        setSelectedFacilityId(facilityId);
        setFeaturePanelSearch('');
        setFeaturePanelFilter('all');
        setMobileShowFeatures(true);
    };

    const enrollFromFacilityView = (
        integration: ServerTransferIntegrationParty,
        existing: ServerTransferEnrolledIntegrationParty | undefined
    ) => {
        if (!selectedFacilityId) return;
        integrationPartyApi.enrollIntegrationParty({
            id: integration.id!,
            serverRequestsEnrollIntegrationPartyRequest: {
                facilityId: selectedFacilityId,
                externalId: existing?.externalId ?? '',
                token: existing?.token ?? '',
                apiSecret: existing?.apiSecret ?? '',
                isActive: !existing?.isActive
            }
        }).then(async () => {
            revalidate();
            await refreshSingleEnrollment(selectedFacilityId, integration.id!);
        });
    };

    const editFromFacilityView = (
        integration: ServerTransferIntegrationParty,
        existing: ServerTransferEnrolledIntegrationParty
    ) => {
        setEditInitialData({ ...existing, facilityId: selectedFacilityId });
        setEditIntegrationParty(integration);
        setEditIntegrationId(integration.id);
        setShowEditModal(true);
    };

    // ── Derived data ──────────────────────────────────────────────────────────

    const isPushFromThirdParty = (integration?: ServerTransferIntegrationParty) =>
        integration?.origin === 'PushFromThirdParty';
    const isPullFromThirdParty = (integration?: ServerTransferIntegrationParty) =>
        integration?.origin === 'PullFromThirdParty';
    const isPullByThirdParty = (integration?: ServerTransferIntegrationParty) =>
        integration?.origin === 'PullByThirdParty';
    const isCredentialedIntegration = (origin?: string | null) =>
        origin === 'ThirdParty' || origin === 'PushFromThirdParty' || origin === 'PullFromThirdParty' || origin === 'PullByThirdParty';
    const isThirdParty = isCredentialedIntegration(selectedIntegration?.origin);
    const enabledCount = facilities.filter(f => f.isActive).length;

    const filteredFacilities = facilities.filter(f => {
        if (facilitySearch && !f.facilityName?.toLowerCase().includes(facilitySearch.toLowerCase())) return false;
        if (facilityFilter === 'active' && !f.isActive) return false;
        if (facilityFilter === 'inactive' && f.isActive) return false;
        return true;
    });

    const filteredIntegrations = integrations.filter(i =>
        !featureSearch || i.name?.toLowerCase().includes(featureSearch.toLowerCase())
    );

    // Unique facilities from the enrollment cache for facility-view left panel
    const facilityList = useMemo(() => {
        const result: { facilityId: number; facilityName: string }[] = [];
        for (const [facilityId, items] of enrollmentCache.entries()) {
            const name = items[0]?.facilityName ?? '';
            result.push({ facilityId, facilityName: name });
        }
        return result.sort((a, b) => a.facilityName.localeCompare(b.facilityName));
    }, [enrollmentCache]);

    const filteredFacilityList = facilityList.filter(f =>
        !facilityListSearch || f.facilityName.toLowerCase().includes(facilityListSearch.toLowerCase())
    );

    const selectedFacilityName = facilityList.find(f => f.facilityId === selectedFacilityId)?.facilityName;

    // Enrollments for the selected facility keyed by integrationPartyId
    const enrollmentsByIntegrationId = useMemo(() => {
        const map = new Map<number, ServerTransferEnrolledIntegrationParty>();
        const items = selectedFacilityId != null ? (enrollmentCache.get(selectedFacilityId) ?? []) : [];
        for (const e of items) {
            if (e.integrationPartyId != null) map.set(e.integrationPartyId, e);
        }
        return map;
    }, [enrollmentCache, selectedFacilityId]);

    const facilityActiveCount = useMemo(
        () => integrations.filter(i => enrollmentsByIntegrationId.get(i.id!)?.isActive).length,
        [integrations, enrollmentsByIntegrationId]
    );

    const filteredIntegrationsForFacility = integrations.filter(i => {
        if (featurePanelSearch && !i.name?.toLowerCase().includes(featurePanelSearch.toLowerCase())) return false;
        const enrollment = enrollmentsByIntegrationId.get(i.id!);
        if (featurePanelFilter === 'active' && !enrollment?.isActive) return false;
        if (featurePanelFilter === 'inactive' && enrollment?.isActive) return false;
        return true;
    });

    // ── Shared sub-components ─────────────────────────────────────────────────

    const OriginBadge = ({ integration, origin, enabledInFacilityView }: { integration?: ServerTransferIntegrationParty; origin?: string | null; enabledInFacilityView?: boolean }) => {
        const integrationOrigin = integration?.origin ?? origin;
        const isIntegration = isCredentialedIntegration(integrationOrigin);
        const usePrimary = enabledInFacilityView === true;
        return (
            <Badge bg={usePrimary ? 'primary' : 'secondary'} className="fw-normal">
                {integrationOrigin === 'PushFromThirdParty' ? 'Push API' : integrationOrigin === 'PullFromThirdParty' ? 'Pull API' : integrationOrigin === 'PullByThirdParty' ? 'Partner Pull' : isIntegration ? 'Integration' : 'Feature'}
            </Badge>
        );
    };

    const FacilityInitial = ({ name, enabled }: { name?: string | null; enabled?: boolean }) => {
        const char = name?.trim()?.[0] ?? '?';
        return (
            <div style={{
                width: 34, height: 34, borderRadius: '50%',
                background: enabled ? 'var(--bs-primary)' : '#f1f3f5',
                color: enabled ? '#fff' : '#495057',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontWeight: 700, fontSize: 13, flexShrink: 0,
            }}>
                {char.toUpperCase()}
            </div>
        );
    };

    // ── By-feature panels ─────────────────────────────────────────────────────

    const featureList = (
        <div style={{ display: 'flex', flexDirection: 'column' }}>
            <div style={{ padding: '12px 16px 6px', fontSize: '0.72rem', fontWeight: 600, color: '#6c757d', textTransform: 'uppercase', letterSpacing: '0.08em' }}>
                Select a Feature
            </div>
            <div style={{ padding: '0 8px 8px' }}>
                <Form.Control
                    type="search"
                    placeholder="Search..."
                    value={featureSearch}
                    onChange={e => setFeatureSearch(e.target.value)}
                    size="sm"
                />
            </div>
            <div style={{ padding: '0 8px 12px', display: 'flex', flexDirection: 'column', gap: 6 }}>
                {filteredIntegrations.map(integration => {
                    const isSelected = selectedIntegration?.id === integration.id;
                    return (
                        <div
                            key={integration.id}
                            onClick={() => onSelectIntegration(integration)}
                            style={{
                                padding: '12px 14px',
                                borderRadius: 8,
                                border: isSelected ? '2px solid #FC5EB4' : '1px solid #e9ecef',
                                background: isSelected ? '#fff9fc' : '#fff',
                                cursor: 'pointer',
                                transition: 'border-color 150ms, background 150ms',
                            }}
                        >
                            <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', marginBottom: integration.description ? 4 : 0 }}>
                                <span style={{ fontWeight: 600, fontSize: '0.9rem', minWidth: 0 }}>{integration.name}</span>
                                <OriginBadge integration={integration} />
                            </div>
                            {integration.description && (
                                <div style={{ fontSize: '0.78rem', color: '#6c757d', lineHeight: 1.4 }}>
                                    {integration.description}
                                </div>
                            )}
                        </div>
                    );
                })}
            </div>
        </div>
    );

    const facilityPanel = (
        <div style={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0, overflow: 'hidden' }}>
            {selectedIntegration ? (
                <>
                    <div style={{ padding: '14px 20px 12px', borderBottom: '1px solid #dee2e6' }}>
                        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12 }}>
                            <div>
                                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 3 }}>
                                    <span style={{ fontWeight: 700, fontSize: '1.05rem' }}>{selectedIntegration.name}</span>
                                    <OriginBadge integration={selectedIntegration} />
                                </div>
                                {selectedIntegration.description && (
                                    <div style={{ fontSize: '0.8rem', color: '#6c757d' }}>{selectedIntegration.description}</div>
                                )}
                            </div>
                            <div style={{ flexShrink: 0 }}>
                                <span style={{ fontSize: '0.82rem', color: '#6c757d', whiteSpace: 'nowrap' }}>
                                    {enabledCount} / {facilities.length} on
                                </span>
                            </div>
                        </div>
                    </div>

                    <div style={{ padding: '10px 16px 6px', display: 'flex', gap: 8, alignItems: 'center' }}>
                        <Form.Control
                            type="search"
                            placeholder="Search facilities..."
                            value={facilitySearch}
                            onChange={e => setFacilitySearch(e.target.value)}
                            size="sm"
                            style={{ flex: 1 }}
                        />
                        <div className="btn-group btn-group-sm" style={{ flexShrink: 0 }}>
                            {(['all', 'active', 'inactive'] as const).map(value => (
                                <button
                                    key={value}
                                    type="button"
                                    className={`btn ${facilityFilter === value ? 'btn-main' : 'btn-outline-secondary'}`}
                                    onClick={() => setFacilityFilter(value)}
                                    style={{ fontSize: '0.75rem', textTransform: 'capitalize' }}
                                >
                                    {value}
                                </button>
                            ))}
                        </div>
                    </div>

                    <div style={{ overflowY: 'auto', flexGrow: 1 }}>
                        {filteredFacilities.map(facility => (
                            <div
                                key={facility.facilityId}
                                className="enrollment-list-row"
                                style={{
                                    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                                    padding: '10px 20px', borderBottom: '1px solid #f1f3f5',
                                }}
                            >
                                <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                                    <FacilityInitial name={facility.facilityName} enabled={!!facility.isActive} />
                                    <div>
                                        <span style={{ fontSize: '0.9rem' }}>{facility.facilityName}</span>
                                        {isThirdParty && (
                                            <div style={{ display: 'flex', gap: 4, marginTop: 4, flexWrap: 'wrap', borderTop: '1px solid #f1f3f5', paddingTop: 4 }}>
                                                {facility.token && <Badge bg="light" text="secondary" className="fw-normal border" style={{ fontSize: '0.65rem' }}>Token</Badge>}
                                                {facility.apiSecret && <Badge bg="light" text="secondary" className="fw-normal border" style={{ fontSize: '0.65rem' }}>Secret</Badge>}
                                                {facility.externalId && <Badge bg="light" text="secondary" className="fw-normal border" style={{ fontSize: '0.65rem' }}>Ext ID</Badge>}
                                            </div>
                                        )}
                                    </div>
                                </div>
                                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                                    {isThirdParty && (facility.isActive || isPullByThirdParty(selectedIntegration)) && (
                                        <Button
                                            size="sm"
                                            variant="outline-secondary"
                                            onClick={() => editIntegration(facility)}
                                            style={{ fontSize: '0.78rem', padding: '2px 10px' }}
                                        >
                                            Settings
                                        </Button>
                                    )}
                                    <Form.Check
                                        type="switch"
                                        id={`toggle-${facility.facilityId}`}
                                        checked={!!facility.isActive}
                                        onChange={() => enrollIntegration(facility)}
                                        className="mb-0"
                                        style={{ cursor: 'pointer' }}
                                    />
                                </div>
                            </div>
                        ))}
                    </div>
                </>
            ) : (
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100%', gap: 16 }}>
                    <div style={{ width: 72, height: 72, borderRadius: '50%', backgroundColor: '#fce4ec' }} />
                    <span style={{ color: '#adb5bd', fontSize: '0.9rem' }}>Select a feature to view facilities</span>
                </div>
            )}
        </div>
    );

    // ── By-facility panels ────────────────────────────────────────────────────

    const facilityListPanel = (
        <div style={{ display: 'flex', flexDirection: 'column' }}>
            <div style={{ padding: '12px 16px 6px', fontSize: '0.72rem', fontWeight: 600, color: '#6c757d', textTransform: 'uppercase', letterSpacing: '0.08em' }}>
                Select a Facility
            </div>
            <div style={{ padding: '0 8px 8px' }}>
                <Form.Control
                    type="search"
                    placeholder="Search..."
                    value={facilityListSearch}
                    onChange={e => setFacilityListSearch(e.target.value)}
                    size="sm"
                />
            </div>
            <div style={{ padding: '0 8px 12px', display: 'flex', flexDirection: 'column', gap: 6 }}>
                {filteredFacilityList.map(f => {
                    const isSelected = selectedFacilityId === f.facilityId;
                    const facilityItems = enrollmentCache.get(f.facilityId) ?? [];
                    const activeCount = integrations.filter(i =>
                        facilityItems.some(e => e.integrationPartyId === i.id && e.isActive)
                    ).length;
                    return (
                        <div
                            key={f.facilityId}
                            onClick={() => onSelectFacility(f.facilityId)}
                            style={{
                                padding: '12px 14px',
                                borderRadius: 8,
                                border: isSelected ? '2px solid #FC5EB4' : '1px solid #e9ecef',
                                background: isSelected ? '#fff9fc' : '#fff',
                                cursor: 'pointer',
                                transition: 'border-color 150ms, background 150ms',
                                display: 'flex', alignItems: 'center', gap: 10,
                            }}
                        >
                            <FacilityInitial name={f.facilityName} />
                            <div style={{ flex: 1, minWidth: 0 }}>
                                <div style={{ fontWeight: 600, fontSize: '0.9rem' }}>{f.facilityName}</div>
                                <div style={{ fontSize: '0.75rem', color: '#6c757d' }}>{activeCount} feature{activeCount !== 1 ? 's' : ''} on</div>
                            </div>
                        </div>
                    );
                })}
                {filteredFacilityList.length === 0 && (
                    <div style={{ padding: '12px 6px', color: '#adb5bd', fontSize: '0.85rem', textAlign: 'center' }}>
                        No facilities found
                    </div>
                )}
            </div>
        </div>
    );

    const featurePanelForFacility = (
        <div style={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0, overflow: 'hidden' }}>
            {selectedFacilityId != null ? (
                <>
                    <div style={{ padding: '14px 20px 12px', borderBottom: '1px solid #dee2e6' }}>
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                                <FacilityInitial name={selectedFacilityName} />
                                <span style={{ fontWeight: 700, fontSize: '1.05rem' }}>{selectedFacilityName}</span>
                            </div>
                            <span style={{ fontSize: '0.82rem', color: '#6c757d', whiteSpace: 'nowrap', flexShrink: 0 }}>
                                {facilityActiveCount} / {integrations.length} on
                            </span>
                        </div>
                    </div>

                    <div style={{ padding: '10px 16px 6px', display: 'flex', gap: 8, alignItems: 'center' }}>
                        <Form.Control
                            type="search"
                            placeholder="Search features..."
                            value={featurePanelSearch}
                            onChange={e => setFeaturePanelSearch(e.target.value)}
                            size="sm"
                            style={{ flex: 1 }}
                        />
                        <div className="btn-group btn-group-sm" style={{ flexShrink: 0 }}>
                            {(['all', 'active', 'inactive'] as const).map(value => (
                                <button
                                    key={value}
                                    type="button"
                                    className={`btn ${featurePanelFilter === value ? 'btn-main' : 'btn-outline-secondary'}`}
                                    onClick={() => setFeaturePanelFilter(value)}
                                    style={{ fontSize: '0.75rem', textTransform: 'capitalize' }}
                                >
                                    {value}
                                </button>
                            ))}
                        </div>
                    </div>

                    <div style={{ overflowY: 'auto', flexGrow: 1 }}>
                        {filteredIntegrationsForFacility.map(integration => {
                            const enrollment = enrollmentsByIntegrationId.get(integration.id!);
                            const isTP = isCredentialedIntegration(integration.origin);
                            return (
                                <div
                                    key={integration.id}
                                    className="enrollment-list-row"
                                    style={{
                                        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                                        padding: '10px 20px', borderBottom: '1px solid #f1f3f5',
                                    }}
                                >
                                    <div style={{ minWidth: 0 }}>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                                            <span style={{ fontSize: '0.9rem', fontWeight: 500 }}>{integration.name}</span>
                                            <OriginBadge integration={integration} enabledInFacilityView={!!enrollment?.isActive} />
                                        </div>
                                        {isTP && enrollment?.isActive && (
                                            <div style={{ display: 'flex', gap: 4, marginTop: 4, flexWrap: 'wrap', borderTop: '1px solid #f1f3f5', paddingTop: 4 }}>
                                                {enrollment.token && <Badge bg="light" text="secondary" className="fw-normal border" style={{ fontSize: '0.65rem' }}>Token</Badge>}
                                                {enrollment.apiSecret && <Badge bg="light" text="secondary" className="fw-normal border" style={{ fontSize: '0.65rem' }}>Secret</Badge>}
                                                {enrollment.externalId && <Badge bg="light" text="secondary" className="fw-normal border" style={{ fontSize: '0.65rem' }}>Ext ID</Badge>}
                                            </div>
                                        )}
                                    </div>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexShrink: 0 }}>
                                        {((isTP && enrollment?.isActive) || isPullByThirdParty(integration)) && (
                                            <Button
                                                size="sm"
                                                variant="outline-secondary"
                                                onClick={() => editFromFacilityView(integration, enrollment ?? {
                                                    facilityId: selectedFacilityId!,
                                                    integrationPartyId: integration.id,
                                                    externalId: '',
                                                    token: '',
                                                    apiSecret: '',
                                                    isActive: false,
                                                })}
                                                style={{ fontSize: '0.78rem', padding: '2px 10px' }}
                                            >
                                                Settings
                                            </Button>
                                        )}
                                        <Form.Check
                                            type="switch"
                                            id={`fac-toggle-${integration.id}`}
                                            checked={!!enrollment?.isActive}
                                            onChange={() => enrollFromFacilityView(integration, enrollment)}
                                            className="mb-0"
                                            style={{ cursor: 'pointer' }}
                                        />
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </>
            ) : (
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100%', gap: 16 }}>
                    <div style={{ width: 72, height: 72, borderRadius: '50%', backgroundColor: '#fce4ec' }} />
                    <span style={{ color: '#adb5bd', fontSize: '0.9rem' }}>Select a facility to view features</span>
                </div>
            )}
        </div>
    );

    // ── View toggle ───────────────────────────────────────────────────────────

    const viewToggle = (
        <div className="btn-group btn-group-sm">
            <button
                type="button"
                className={`btn ${viewMode === 'byFeature' ? 'btn-main' : 'btn-outline-secondary'}`}
                onClick={() => handleViewModeChange('byFeature')}
                style={{ fontSize: '0.78rem' }}
            >
                By Feature
            </button>
            <button
                type="button"
                className={`btn ${viewMode === 'byFacility' ? 'btn-main' : 'btn-outline-secondary'}`}
                onClick={() => handleViewModeChange('byFacility')}
                style={{ fontSize: '0.78rem' }}
            >
                By Facility
            </button>
        </div>
    );

    return (
        <Container fluid className="h-100 d-flex flex-column p-0" style={{ overflow: 'hidden' }}>
            <style>{`.enrollment-list-row:hover { background-color: #f8f9fa; transition: background-color 0.15s ease; }
.enrollment-list-row .form-switch { cursor: pointer; }
.enrollment-list-row .form-check-input { cursor: pointer; }`}</style>
            <AdminOperatorIntegrationEnrollModal
                show={showEditModal}
                onSave={onModalSave}
                setShow={setShowEditModal}
                initialData={editInitialData!}
                integrationPartyId={editIntegrationId}
                isPushFromThirdParty={isPushFromThirdParty(editIntegrationParty)}
                isPullFromThirdParty={isPullFromThirdParty(editIntegrationParty)}
                isPullByThirdParty={isPullByThirdParty(editIntegrationParty)}
            />

            {/* Header */}
            <div style={{ padding: '8px 16px 4px', borderBottom: '1px solid #dee2e6', flexShrink: 0 }}>
                <PageBar title={
                    <div className="d-flex flex-column flex-md-row align-items-start align-items-md-center gap-2 gap-md-3">
                        <div className="d-flex align-items-center gap-3">
                            <Button variant="link" className="p-0" onClick={() => nav(-1)}>← Back</Button>
                            <span>Features</span>
                            <span className="d-none d-md-inline">{viewToggle}</span>
                        </div>
                        <span className="d-md-none">{viewToggle}</span>
                    </div>
                } />
            </div>

            {/* Panel area */}
            <div style={{ position: 'relative', flex: '1 1 0', minHeight: 0 }}>
                {viewMode === 'byFeature' ? (
                    <>
                        {/* Desktop: two-panel layout */}
                        <div className="d-none d-md-flex" style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0 }}>
                            <div style={{ width: 270, minWidth: 240, flexShrink: 0, borderRight: '1px solid #dee2e6', overflowY: 'auto' }}>
                                {featureList}
                            </div>
                            <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
                                {facilityPanel}
                            </div>
                        </div>

                        {/* Mobile */}
                        <div className="d-md-none" style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, display: 'flex', flexDirection: 'column' }}>
                            {!mobileShowFacilities ? (
                                <div style={{ overflowY: 'auto', flex: 1 }}>{featureList}</div>
                            ) : (
                                <>
                                    <div style={{ padding: '8px 16px', borderBottom: '1px solid #dee2e6', flexShrink: 0 }}>
                                        <Button variant="link" className="p-0" onClick={() => setMobileShowFacilities(false)}>
                                            ← Back to Features
                                        </Button>
                                    </div>
                                    <div style={{ flex: 1, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
                                        {facilityPanel}
                                    </div>
                                </>
                            )}
                        </div>
                    </>
                ) : (
                    <>
                        {/* Desktop: two-panel layout */}
                        <div className="d-none d-md-flex" style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0 }}>
                            <div style={{ width: 270, minWidth: 240, flexShrink: 0, borderRight: '1px solid #dee2e6', overflowY: 'auto' }}>
                                {facilityListPanel}
                            </div>
                            <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
                                {featurePanelForFacility}
                            </div>
                        </div>

                        {/* Mobile */}
                        <div className="d-md-none" style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, display: 'flex', flexDirection: 'column' }}>
                            {!mobileShowFeatures ? (
                                <div style={{ overflowY: 'auto', flex: 1 }}>{facilityListPanel}</div>
                            ) : (
                                <>
                                    <div style={{ padding: '8px 16px', borderBottom: '1px solid #dee2e6', flexShrink: 0 }}>
                                        <Button variant="link" className="p-0" onClick={() => setMobileShowFeatures(false)}>
                                            ← Back to Facilities
                                        </Button>
                                    </div>
                                    <div style={{ flex: 1, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
                                        {featurePanelForFacility}
                                    </div>
                                </>
                            )}
                        </div>
                    </>
                )}
            </div>
        </Container>
    );
}
